<template>
    <div class="ch">
        <section v-if="!bare" class="ch-card">
            <div class="ch-ring" :class="data && data.score != null ? 'ch-ring-' + band(data.score) : 'ch-ring-none'">
                <svg v-if="data && data.score != null" viewBox="0 0 72 72" aria-hidden="true"><circle cx="36" cy="36" r="31" class="ch-ring-track" /><circle cx="36" cy="36" r="31" class="ch-ring-fill" :stroke-dasharray="ringDash(data.score)" /></svg>
                <span>{{ data && data.score != null ? data.score : '–' }}</span>
            </div>
            <div class="ch-meta">
                <div class="ch-label">Company health</div>
                <div class="ch-score"><strong>{{ data && data.score != null ? data.score : '–' }}</strong><span v-if="data">{{ gapCount }} gap{{ gapCount == 1 ? '' : 's' }} across {{ data.checks.length }} checks</span></div>
                <div class="ch-sub">What's missing from the Playbook: people, teams and the accountability chart. Fix a gap and it drops off the next time you open this tab.</div>
            </div>
            <button type="button" class="ai-btn ai-btn-ghost" :disabled="loading" @click="load()">{{ loading ? 'Checking…' : 'Check again' }}</button>
        </section>

        <!-- bare: the Forge page's Health tile shows the score, so only the explanation and Check again stay here -->
        <div v-if="bare" class="ch-slim">
            <span>What's missing from the Playbook: people, teams and the accountability chart. Fix a gap and it drops off the next time you check.</span>
            <button type="button" class="ai-btn ai-btn-ghost" :disabled="loading" @click="load()">{{ loading ? 'Checking…' : 'Check again' }}</button>
        </div>
        <div v-if="error" class="ch-error" role="alert">{{ error }}</div>
        <div v-else-if="!data" class="ch-loading" role="status">Checking the company…</div>

        <template v-else>
            <section v-for="c in data.checks" :key="c.key" class="ch-check" :class="{ 'ch-check-done': !c.items.length }">
                <button type="button" class="ch-check-head" :aria-expanded="open[c.key] ? 'true' : 'false'" @click="toggle(c.key)">
                    <span class="ch-dot" :class="'ch-dot-' + (c.total ? band(100 * c.ok / c.total) : 'great')" aria-hidden="true"></span>
                    <span class="ch-check-text">
                        <span class="ch-check-title">{{ c.title }}</span>
                        <span class="ch-check-why">{{ c.why }}</span>
                    </span>
                    <span class="ch-count">
                        <template v-if="!c.total">Nothing to check</template>
                        <template v-else-if="!c.items.length">All {{ c.total }} ✓</template>
                        <template v-else>{{ c.ok }} of {{ c.total }}</template>
                    </span>
                    <span v-if="c.items.length" class="ch-chev" aria-hidden="true">{{ open[c.key] ? '▴' : '▾' }}</span>
                </button>
                <div v-if="c.total" class="ch-bar" aria-hidden="true"><span :style="{ width: (100 * c.ok / c.total) + '%' }"></span></div>
                <div v-if="open[c.key] && c.items.length" class="ch-items">
                    <div v-if="c.key == 'role' || c.key == 'checklist'" class="ch-forge">
                        <span>{{ c.key == 'role' ? 'No role sheet that fits yet?' : 'No Powerlist that fits yet?' }}</span>
                        <button type="button" class="ai-btn" @click="forgeNode = c.key == 'role' ? 'sheet' : 'checklist'">{{ c.key == 'role' ? 'Forge a role sheet' : 'Forge a Powerlist' }}</button>
                    </div>
                    <div v-for="(it, i) in shown(c)" :key="i" class="ch-item">
                        <span class="ch-item-name">{{ it.name }}<span v-if="it.team" class="ch-item-team"> · {{ it.team }}</span></span>
                        <span class="ch-item-detail">{{ it.detail || '' }}</span>
                        <router-link v-if="it.kind == 'employee' && c.key != 'chart'" :to="'/employee/' + it.id" class="ch-fix">{{ fixLabel(c.key) }}</router-link>
                        <router-link v-else-if="c.key == 'chart'" to="/accountability-chart" class="ch-fix">Open the chart</router-link>
                        <button v-else-if="c.key == 'scorecard'" type="button" class="ch-fix ch-fix-forge" @click="openForge('scorecard', it.name)">Draft with the Forge</button>
                        <button v-else-if="c.key == 'rocks'" type="button" class="ch-fix ch-fix-forge" @click="openForge('rocks', it.name)">Draft with the Forge</button>
                    </div>
                    <button v-if="c.items.length > limit && !all[c.key]" type="button" class="ch-more" @click="all[c.key] = true">Show all {{ c.items.length }}</button>
                </div>
            </section>
            <p class="ch-note">Coming next: checks against the Ironbook methodology itself, such as the processes a company like yours should have written down.</p>
        </template>

        <forge-team v-if="forge" modal :only="[ forge.kind ]" :start-kind="forge.kind" :preset-team="forge.team" @applied="load()" @close="forge = null" />
        <ai-create-node v-if="forgeNode" :node-type="forgeNode" @close="forgeNode = ''" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ForgeTeam from './ForgeTeam.vue';
import AICreateNode from './AICreateNode.vue';

// The Forge's Company health tab (API routes/ai.ts GET /company-health, services/companyhealth.ts): gaps in role
// sheets, Powerlists, reviews, employee records, the accountability chart, team scorecards and Rocks. Each gap
// links to where it's fixed; scorecards and Rocks open the Forge for that team. Team Hub Admins only.
export default defineComponent({
    name: 'CompanyHealth',
    components: { ForgeTeam, AiCreateNode: AICreateNode },
    props: { bare: { type: Boolean, default: false } },
    emits: [ 'summary' ],
    data() {
        return {
            data: null as any, loading: false, error: '',
            open: {} as any, all: {} as any, limit: 12,
            forge: null as any, forgeNode: ''
        };
    },
    computed: {
        gapCount(): number { return this.data ? this.data.checks.reduce((n: number, c: any) => n + (c.key == 'chart' && c.items.length == 1 && c.items[0].kind == 'company' ? 1 : c.items.length), 0) : 0; }
    },
    async mounted() { await this.load(); },
    methods: {
        async load() {
            this.loading = true;
            this.error = '';
            try {
                this.data = (await axios.get(`${this.$store.state.apiUrl}/api/ai/company-health`)).data;
                this.$emit('summary', { score: this.data.score, gaps: this.gapCount, checks: this.data.checks.length });
                // Open the first check that has gaps
                if(!Object.keys(this.open).length) {
                    const first = this.data.checks.find((c: any) => c.items.length);
                    if(first) this.open[first.key] = true;
                }
            } catch(e: any) {
                this.error = e?.response?.status == 403 ? 'Only Team Hub admins can see Company health.' : 'Company health couldn\'t be checked. Please try again.';
            }
            this.loading = false;
        },
        toggle(key: string) { this.open[key] = !this.open[key]; },
        shown(c: any): any[] { return this.all[c.key] ? c.items : c.items.slice(0, this.limit); },
        fixLabel(key: string): string { return key == 'review' ? 'Review' : key == 'info' ? 'Fill in' : 'Open profile'; },
        openForge(kind: string, team: string) { this.forge = { kind, team }; },
        band(score: number): string { return score >= 85 ? 'great' : score >= 70 ? 'good' : score >= 50 ? 'fair' : 'poor'; },
        ringDash(score: number): string { const c = 2 * Math.PI * 31; return `${(Math.max(0, Math.min(100, score)) / 100) * c} ${c}`; }
    }
});
</script>

<style scoped>
.ch { display: grid; gap: 14px; }
.ch-slim { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; justify-content: space-between; color: #6f655c; font-size: 14px; }
.ch-slim span { flex: 1; min-width: 220px; }
.ch-card { position: relative; display: flex; align-items: center; gap: 22px; flex-wrap: wrap; background: linear-gradient(180deg, #fff8f1 0, #fff 120px); border-radius: 16px; padding: 22px 26px; box-shadow: 0 2px 14px rgba(124, 58, 18, .08), 0 0 0 1px #f6e3d3; overflow: hidden; }
.ch-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: var(--ai-grad); }
.ch-ring { position: relative; width: 84px; height: 84px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.ch-ring svg { position: absolute; inset: 0; transform: rotate(-90deg); }
.ch-ring span { font-size: 26px; font-weight: 800; color: #1f1712; }
.ch-ring-track { fill: none; stroke: #f3ece5; stroke-width: 7; }
.ch-ring-fill { fill: none; stroke-width: 7; stroke-linecap: round; transition: stroke-dasharray .6s ease; }
.ch-ring-poor .ch-ring-fill { stroke: #dc2626; }
.ch-ring-fair .ch-ring-fill { stroke: #e0a526; }
.ch-ring-good .ch-ring-fill { stroke: #65a30d; }
.ch-ring-great .ch-ring-fill { stroke: #16a34a; }
.ch-ring-none { border: 7px solid #f3ece5; border-radius: 50%; box-sizing: border-box; }
.ch-ring-none span { color: #b3a89c; }
.ch-meta { flex: 1; min-width: 200px; }
.ch-label { font-size: 12.5px; color: #8a7f75; font-weight: 600; }
.ch-score { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.ch-score strong { font-size: 30px; font-weight: 800; color: #1f1712; }
.ch-score span { font-size: 14px; color: #6f655c; }
.ch-sub { font-size: 13.5px; color: #6f655c; margin-top: 2px; max-width: 62ch; }
.ch-error { color: #a52c26; }
.ch-loading { color: #8a7f75; padding: 10px 2px; }
.ch-check { background: #fff; border-radius: 14px; box-shadow: 0 1px 6px rgba(124, 58, 18, .06), 0 0 0 1px #f1e6dc; overflow: hidden; }
.ch-check-head { width: 100%; display: flex; align-items: center; gap: 14px; padding: 16px 18px 12px; background: none; border: 0; cursor: pointer; text-align: left; font: inherit; color: inherit; }
.ch-check-done .ch-check-head { cursor: default; }
.ch-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.ch-dot-poor { background: #dc2626; } .ch-dot-fair { background: #e0a526; } .ch-dot-good { background: #65a30d; } .ch-dot-great { background: #16a34a; }
.ch-check-text { flex: 1; min-width: 0; display: grid; gap: 2px; }
.ch-check-title { font-weight: 700; font-size: 16px; color: #1f1712; }
.ch-check-why { font-size: 13px; color: #8a7f75; }
.ch-count { font-weight: 700; font-size: 14px; color: #3a2f27; white-space: nowrap; font-variant-numeric: tabular-nums; }
.ch-check-done .ch-count { color: #16a34a; }
.ch-chev { color: #8a7f75; font-size: 14px; }
.ch-bar { height: 4px; background: #f3ece5; margin: 0 18px 14px; border-radius: 2px; overflow: hidden; }
.ch-bar span { display: block; height: 100%; background: linear-gradient(90deg, #f59a2f, #e8661c); border-radius: 2px; }
.ch-check-done .ch-bar span { background: #16a34a; }
.ch-items { border-top: 1px solid #f3ece5; padding: 6px 18px 14px; display: grid; }
.ch-forge { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; background: #fff8f1; border-radius: 10px; padding: 10px 14px; margin: 8px 0; font-size: 14px; color: #5c4636; }
.ch-item { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.6fr) auto; gap: 12px; align-items: center; padding: 9px 0; border-bottom: 1px solid #f7f1ec; font-size: 14.5px; }
.ch-item:last-of-type { border-bottom: none; }
.ch-item-name { font-weight: 600; color: #2b221b; min-width: 0; overflow-wrap: anywhere; }
.ch-item-team { font-weight: 400; color: #8a7f75; }
.ch-item-detail { color: #6f655c; font-size: 13.5px; min-width: 0; overflow-wrap: anywhere; }
.ch-fix { justify-self: end; font-size: 13.5px; font-weight: 700; color: #c2410c; background: none; border: 0; cursor: pointer; padding: 0; font-family: inherit; white-space: nowrap; }
.ch-fix:hover { text-decoration: underline; }
.ch-more { justify-self: start; margin-top: 8px; background: none; border: 0; color: #c2410c; font-weight: 700; cursor: pointer; padding: 0; font: inherit; font-size: 14px; }
.ch-note { font-size: 13.5px; color: #8a7f75; margin: 4px 2px 0; }
@media (max-width: 640px) {
    .ch-card { padding: 18px; }
    .ch-item { grid-template-columns: minmax(0, 1fr) auto; }
    .ch-item-detail { grid-column: 1 / -1; grid-row: 2; }
}
</style>
